import React, { useState, useEffect } from 'react';
import { whatsappAPI } from '../services/api';

const TEMPLATE_LABELS = {
  student_registration:     'Student Registration Confirmation',
  demo_reminder_24h:        'Demo Reminder (24h)',
  demo_reminder_1h:         'Demo Reminder (1h)',
  demo_thankyou:            'Demo Thank You',
  scholarship_eligible:     'Scholarship — Eligible',
  scholarship_not_eligible: 'Scholarship — Not Eligible',
  followup_reminder:        'Follow-up Reminder',
  lead_assigned:            'Lead Assigned',
  franchise_confirmation:   'Franchise Confirmation',
  outreach_general:         'General Outreach',
};

export default function WhatsAppModal({ targetId, targetType, phone, recipientName, onClose }) {
  const [settings, setSettings] = useState(null);
  const [templateKey, setTemplateKey] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    whatsappAPI.getSettings()
      .then(r => {
        setSettings(r.data.data);
        // Pre-select first configured template
        const templates = r.data.data?.templates || {};
        const firstKey = Object.entries(templates).find(([, v]) => v)?.[0];
        if (firstKey) setTemplateKey(firstKey);
      })
      .catch(() => setSettings(null))
      .finally(() => setLoading(false));
  }, []);

  const configuredTemplates = settings?.templates
    ? Object.entries(settings.templates).filter(([, v]) => v)
    : [];

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const payload = {
        phone,
        recipient_name: recipientName,
        template_key: useCustom ? 'outreach_general' : templateKey,
        variables: [],
        ...(useCustom && { custom_message: customMessage }),
      };
      const sendFn = targetType === 'student'
        ? whatsappAPI.sendToStudent(targetId, payload)
        : whatsappAPI.sendToLead(targetId, payload);
      const res = await sendFn;
      setResult(res.data);
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.message || 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 480, width: '95%', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 18, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, background: '#25d366', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>💬</div>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>Send WhatsApp</h3>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{recipientName} · {phone}</p>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>Loading settings...</div>
        ) : !settings?.is_configured ? (
          <div style={{ padding: '12px 16px', background: '#fee2e2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
            WhatsApp is not configured. Go to Settings → WhatsApp to set it up.
          </div>
        ) : (
          <>
            {settings?.send_outreach_messages && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button
                  onClick={() => setUseCustom(false)}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${!useCustom ? '#6366f1' : '#e2e8f0'}`, background: !useCustom ? '#eef2ff' : '#fff', color: !useCustom ? '#6366f1' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                >Template</button>
                <button
                  onClick={() => setUseCustom(true)}
                  style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${useCustom ? '#6366f1' : '#e2e8f0'}`, background: useCustom ? '#eef2ff' : '#fff', color: useCustom ? '#6366f1' : '#64748b', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                >Custom Message</button>
              </div>
            )}

            {!useCustom && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Template</label>
                {configuredTemplates.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>No templates configured. Add template IDs in WhatsApp Settings.</div>
                ) : (
                  <select value={templateKey} onChange={e => setTemplateKey(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                    {configuredTemplates.map(([k]) => (
                      <option key={k} value={k}>{TEMPLATE_LABELS[k] || k}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {useCustom && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Custom Message</label>
                <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)} rows={4} placeholder="Type your message..."
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            )}

            {result && (
              <div style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 8, fontSize: 13,
                background: result.success ? '#dcfce7' : '#fee2e2',
                color: result.success ? '#16a34a' : '#dc2626',
              }}>
                {result.success ? `✓ Sent! Message ID: ${result.messageId || 'N/A'}` : `✗ Failed: ${result.error}`}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {result?.success ? 'Close' : 'Cancel'}
              </button>
              {!result?.success && (
                <button onClick={handleSend} disabled={sending || (!useCustom && !templateKey) || (useCustom && !customMessage)}
                  style={{ background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14, opacity: sending ? 0.7 : 1 }}>
                  {sending ? 'Sending...' : 'Send'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
